import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "../../app/hooks";
import { loadMarketplaceCompanies, MarketplaceCompany } from "./marketPlaceApi"; // <- twój thunk
import {
    Avatar, Badge, Box, Button, Chip, CssBaseline, Divider, FormControlLabel,
    IconButton, InputAdornment, List, ListItem, ListItemAvatar, ListItemButton,
    ListItemText, MenuItem, Paper, Select, Stack, Switch, TextField, ToggleButton,
    ToggleButtonGroup, Tooltip, Typography
} from "@mui/material";
import {
    Search as SearchIcon, FilterList as FilterListIcon, Send as SendIcon,
    Star as StarIcon, Business as BusinessIcon, OpenInNew as OpenInNewIcon,
    Sort as SortIcon
} from "@mui/icons-material";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import { LoadingContainer } from "../../app/LoadingContainer";
import { ErrorContainer } from "../../app/ErrorContainer";
import { MarketPlaceCompanyCard } from "./MarketPlaceCompanyCard";
import { CompanyDetailsDialog } from "./CompanyDetailsDialog";
import { alpha } from "@mui/material/styles";
import { MARKET_REGION_LABEL, MarketRegionEnum } from "../administration/companyProfile/marketRegion";
import { StructureTreeView } from "../documents/templates/utils/StructureTreeView";
import { AppId } from "../../app/appTypes";
import { EnumMultiSelect } from "../../app/components/EnumMultiSelect";
import { QUICK_TAG_LABEL, QUICK_TAG_OPTIONS, QuickTagEnum } from "../administration/companyProfile/enums/QuickTagEnum";
import { Localized, useLocalization } from "@fluent/react";


function useDebouncedValue<T>(value: T, ms = 300) {
    const [v, setV] = useState(value);
    useEffect(() => {
        const h = setTimeout(() => setV(value), ms);
        return () => clearTimeout(h);
    }, [value, ms]);
    return v;
}

function hash01(str: string) {
    let h = 0;
    for (let i = 0; i < (str ?? "").length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
    return (h % 1000) / 1000; // 0..1
}

function getAccentColor(c: { name?: string; countryCode?: string | null; country?: string | null }) {
    const basis = (c.countryCode || c.country || c.name || "X").toUpperCase();
    const l = 35 + hash01(basis) * 20; // jasność 35–55%
    const s = 70;                      // nasycenie stałe
    const hue = 140;                   // ZIELONY
    return `hsl(${hue}, ${s}%, ${l}%)`;
}

export default function MarketplaceCatalog() {
    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();

    const [search, setSearch] = useState("");
    const debouncedSearch = useDebouncedValue(search, 300);

    const [favorites, setFavorites] = useState<string[]>(() => {
        try {
            return JSON.parse(localStorage.getItem("mp_favorites") || "[]");
        } catch { return []; }
    });
    const [onlyFav, setOnlyFav] = useState(false);

    const [view, setView] = useState<"grid" | "list">(
        () => (localStorage.getItem("mp_view") as "grid" | "list") || "grid"
    );
    useEffect(() => { localStorage.setItem("mp_view", view); }, [view]);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [data, setData] = useState<MarketplaceCompany[] | null>(null);
    const [error, setError] = useState<any>(null);
    const [isError, setIsError] = useState<boolean>(false);
    const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
    const [selectedCompany, setSelectedCompany] = useState<MarketplaceCompany | null>(null);
    const [structureId, setStructureId] = useState<AppId | undefined>(undefined);
    const [quickTagIds, setQuickTagIds] = useState<QuickTagEnum[] | null>(null);

    useEffect(() => {
        setIsLoading(true);
        dispatch(loadMarketplaceCompanies({
            q: debouncedSearch || undefined,
            sortDir: sortDir,
            page: 1,
            pageSize: 100,
            structureId: structureId
        }))
            .unwrap()
            .then(res => setData(res.items ?? []))
            .catch(err => { console.error(err); setIsError(true); setError(err); })
            .finally(() => setIsLoading(false));
    }, [dispatch, debouncedSearch, sortDir, structureId]);

    // favorites toggle
    const toggleFavorite = (id: number) => {
        setFavorites(prev => {
            const s = String(id);
            const next = prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s];
            localStorage.setItem("mp_favorites", JSON.stringify(next));
            return next;
        });
    };

    const filtered = useMemo(() => {
        let list = (data ?? []);
        if (onlyFav) list = list.filter(c => favorites.includes(String(c.id)));
        if (quickTagIds && quickTagIds.length > 0) {
            list = list.filter(item => {
                const tags = item.quickTags ?? [];
                return quickTagIds.every(tagId => tags.includes(tagId));
            });
        }
        list = [...list].sort((a, b) => {
            const cmp = (a.name ?? "").localeCompare(b.name ?? "", "pl", { sensitivity: "base" });
            return sortDir === "asc" ? cmp : -cmp;
        });

        return list;
    }, [data, onlyFav, quickTagIds, favorites, sortDir, structureId]);


    const handleStructureSelected = (nodeId: AppId) => {
        setStructureId(nodeId);
    };

    if (isError) return <ErrorContainer error={error} />;

    return (
        <Box sx={{ display: "flex" }}>
            <CssBaseline />

            {/* LEFT FILTERS */}
            <Paper
                variant="outlined"
                sx={{
                    p: 1,
                    width: 440,
                    mr: 2,
                    borderRadius: 2,
                    position: "sticky",
                    top: 16,
                    alignSelf: "flex-start",
                    backgroundColor: "background.paper",
                }}  >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <FilterListIcon />
                    <Typography variant="subtitle1" fontWeight={700}>Filters</Typography>
                </Stack>
                <Box sx={{
                    mt: 1, border: "1px solid", borderColor: "divider", borderRadius: 1,
                    p: 1, maxHeight: "calc(100vh - 220px)", overflow: "auto"
                }}>
                    <Typography variant="body2" color="text.secondary"></Typography>
                    <StructureTreeView
                        editable={false}
                        multiselect={false}
                        showCounter={false}
                        onStructureSelected={handleStructureSelected}
                        expandToId={structureId ?? null}
                    />
                </Box>

                <Divider sx={{ my: 2 }} />

                <FormControlLabel
                    control={<Switch checked={onlyFav} onChange={(e) => setOnlyFav(e.target.checked)} />}
                    label="Only favorites"
                />

                <Divider sx={{ my: 2 }} />

                <Stack spacing={1}>
                    <EnumMultiSelect
                        label={l10n.getString("companies-quick-tags", null, "_Quick tags")}
                        value={quickTagIds}
                        size="small"
                        options={QUICK_TAG_OPTIONS.map(o => ({ id: o.id, name: o.label }))}
                        onChange={(ids) =>
                            setQuickTagIds(ids as QuickTagEnum[] | null)
                        }
                    />
                </Stack>
            </Paper>

            {/* MAIN CONTENT */}
            <Box component="main" sx={{ flexGrow: 1, p: 2 }}>

                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.5}
                    alignItems={{ xs: "stretch", md: "center" }}
                    sx={{ mb: 1.5 }}
                >
                    <Typography variant="h5" sx={{ mr: { md: 1 } }}>Marketplace</Typography>

                    <TextField
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search companies, country, city…"
                        size="small"
                        sx={{ flex: 1, maxWidth: 700 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start"><SearchIcon /></InputAdornment>
                            ),
                        }}
                    />

                    <Tooltip title="Toggle favorites filter">
                        <IconButton onClick={() => setOnlyFav(v => !v)}>
                            <Badge color="error" variant={onlyFav ? "dot" : undefined}>
                                <StarIcon />
                            </Badge>
                        </IconButton>
                    </Tooltip>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <SortIcon />
                        <Select
                            size="small"
                            value={sortDir}
                            onChange={(e) => setSortDir(e.target.value as "asc" | "desc")}
                        >
                            <MenuItem value="asc">Sort: Name (A→Z)</MenuItem>
                            <MenuItem value="desc">Sort: Name (Z→A)</MenuItem>
                        </Select>
                        <ToggleButtonGroup
                            size="small"
                            value={view}
                            exclusive
                            onChange={(_, v) => v && setView(v)}
                            sx={{ ml: { md: 1 } }}
                            aria-label="change view"
                        >
                            <ToggleButton value="grid" aria-label="grid view">
                                <ViewModuleIcon fontSize="small" />
                            </ToggleButton>
                            <ToggleButton value="list" aria-label="list view">
                                <ViewListIcon fontSize="small" />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                </Stack>

                {filtered.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 8 }}>
                        <Typography variant="h6" gutterBottom>
                            <Localized id="companies-marketplace-no-results">
                                _No results
                            </Localized>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <Localized id="companies-marketplace-search-reset-info">
                                _Adjust search criteria or reset filters.
                            </Localized>
                        </Typography>
                        <Button sx={{ mt: 2 }} onClick={() => { 
                                setSearch("");
                                setOnlyFav(false);
                                setStructureId(undefined);
                                setQuickTagIds(null); 
                            }}>
                            <Localized id="reset">
                                _Reset
                            </Localized>
                        </Button>
                    </Box>
                ) : view === "grid" ? (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
                            gap: 2,
                        }}
                    >
                        {filtered.map((c) => (
                            <MarketPlaceCompanyCard
                                key={c.id}
                                company={c}
                                isFavorite={favorites.includes(String(c.id))}
                                onOpen={(co) => setSelectedCompany(co)}
                                toggleFavorite={(id: number) => toggleFavorite(Number(id))}
                            />
                        ))}
                    </Box>
                ) : (
                    // === LISTA ===
                    <List disablePadding component="ul" sx={{ display: "grid", gap: 1.0 }}>
                        {filtered.map((c) => {
                            const accent = getAccentColor(c);
                            return (
                                <Box key={c.id} component="li" sx={{ listStyle: "none" }}>
                                    <Box
                                        onClick={() => setSelectedCompany(c)}
                                        role="button"
                                        tabIndex={0}
                                        sx={{
                                            position: "relative",
                                            display: "grid",
                                            gridTemplateColumns: "auto 1fr auto",
                                            alignItems: "center",
                                            gap: 1.25,
                                            p: 1.25,
                                            pr: 2,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 2,
                                            backgroundColor: "background.paper",
                                            cursor: "pointer",
                                            transition: "box-shadow .2s, background-color .2s, transform .02s",
                                            "&:hover": {
                                                backgroundColor: (t) => alpha(accent, 0.06),
                                                boxShadow: (t) => t.shadows[2],
                                            },
                                            "&:active": { transform: "scale(0.998)" },
                                            "&:focus-visible": {
                                                outline: "none",
                                                boxShadow: (t) => `0 0 0 3px ${alpha(accent, 0.35)}`,
                                            },
                                            "&::before": {
                                                content: '""',
                                                position: "absolute",
                                                left: 0,
                                                top: 0,
                                                bottom: 0,
                                                width: 6,
                                                bgcolor: accent,
                                                borderTopLeftRadius: "inherit",
                                                borderBottomLeftRadius: "inherit",
                                            },
                                        }}
                                    >
                                        {c?.logoUrl ? (
                                            <Box
                                                component="img"
                                                src={c.logoUrl}
                                                alt={c?.name ? `${c.name} logo` : "Company logo"}
                                                loading="lazy"
                                                referrerPolicy="no-referrer"
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    objectFit: "contain",
                                                    display: "block",
                                                }}
                                            />
                                        ) : (
                                            <BusinessIcon fontSize="large" />
                                        )}

                                        {/* tekst */}
                                        <Box sx={{ minWidth: 0 }}>
                                            <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: "wrap" }}>
                                                <Typography fontWeight={700} sx={{ mr: 0.5, overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {c.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                    {c.country}
                                                    {c.city ? ` / ${c.city}` : ""}
                                                    {c.countryCode ? ` (${c.countryCode})` : ""}
                                                </Typography>
                                            </Stack>

                                            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                                                {(c.markets ?? []).map((code) => {
                                                    const label = MARKET_REGION_LABEL[code as MarketRegionEnum];
                                                    if (!label) return null;
                                                    return <Chip key={code} size="small" label={label} />;
                                                })}
                                            </Stack>
                                            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                                                {(c.quickTags ?? []).map((tag) => {
                                                    const label = QUICK_TAG_LABEL[tag as QuickTagEnum];
                                                    if (!label) return null;
                                                    return <Chip color="info" key={tag} size="small" label={label} />;
                                                })}
                                            </Stack>
                                        </Box>

                                        {/* akcje (po prawej) */}
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                            className="row-actions"
                                            sx={{ opacity: 0.75, transition: "opacity .2s", "&:hover": { opacity: 1 } }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <Chip
                                                size="small"
                                                icon={<DevicesOtherIcon />}
                                                label={`Devices: ${c.devicesCount ?? 0}`}
                                                variant="outlined"
                                            />
                                            <Tooltip title="Open details">
                                                <IconButton edge="end" onClick={() => setSelectedCompany(c)}>
                                                    <OpenInNewIcon />
                                                </IconButton>
                                            </Tooltip>

                                        </Stack>
                                    </Box>
                                </Box>
                            );
                        })}
                    </List>

                )}

                <CompanyDetailsDialog
                    companyId={selectedCompany?.id ?? null}
                    open={Boolean(selectedCompany)}
                    onClose={() => setSelectedCompany(null)}
                />
            </Box>
        </Box >
    );
}
